import { useTranslation } from 'react-i18next';
import Button from '@/components/ui/Button';
import { ReturnedPaymentDtoIn } from '@/models/in/ReturnedPaymentDtoIn';
import alertIcon from '@/assets/icons/alert-circle.svg';
import useReturnedPayments from './useReturnedPayments';
import styles from './ReturnedPaymentSection.module.css';

interface ReturnedPaymentSectionProps {
  correlationId: string;
  onUpdateBankAccount: (group: ReturnedPaymentDtoIn) => void;
}

/**
 * HAT-1395: at the top of a request's drawer, in the same yellow box as «נדרשת השלמת מסמכים» - the
 * office handing the request back for something only the coordinator can supply. One line and a
 * button; the form itself is the «עדכון חשבון בנק» window, never squeezed into the drawer.
 *
 * Renders nothing unless the request pays to an account the office deleted for a returned payment.
 */
const ReturnedPaymentSection = ({ correlationId, onUpdateBankAccount }: ReturnedPaymentSectionProps) => {
  const { t } = useTranslation();
  const { groupOf } = useReturnedPayments();
  const group = groupOf(correlationId);

  if (!group) return null;

  const request = group.requests.find((r) => r.correlationId === correlationId);
  const bounced = !!request?.months.some((month) => month.returned);
  const account = `${group.returnedAccount.bankNumber}-${group.returnedAccount.branchNumber}-${group.returnedAccount.accountNumber}`;

  return (
    <section className={styles.section} aria-labelledby={`returned-payment-title-${correlationId}`}>
      <div className={styles.header}>
        <img src={alertIcon} alt="" className={styles.icon} />
        <div className="flex flex-col gap-1">
          <h3 id={`returned-payment-title-${correlationId}`} className="body-md-semibold text-(--font-heading)">
            {bounced ? t('returnedPayment.title') : t('returnedPayment.titleAccountDeleted')}
          </h3>
          <span className="body-sm-regular text-(--font-body-2)">
            {bounced ? t('returnedPayment.boxReturned', { account }) : t('returnedPayment.boxAccountDeleted', { account })}
          </span>
        </div>
      </div>
      <Button variant="primary" className={styles.action} onClick={() => onUpdateBankAccount(group)}>
        {t('returnedPayment.menuLabel')}
      </Button>
    </section>
  );
};

export default ReturnedPaymentSection;
